import { isEmpty, size, get } from 'lodash';
import React from 'react';
import styled from 'styled-components';

import OrderCard from '../../Orders/OrderCard';
import Ticket from '../Ticket';
import TourSelectionForm from '../TourSelectionForm';
import ClientOrderListSkeleton from '../../Clients/ClientOrderListSkeleton';
import ConversationList2 from '../../Chains/ConversationList2';
import { TYPES } from '../TicketType';
import { ChainType, OrderType, TicketType } from 'types/types';
import { ExtraInfoType, OptionsType } from '../types';
import { TourFormOptionsType, TourFormType } from '../TourSelectionForm/types';
import { TicketProps } from '../Ticket/types';
import { IOrderCard } from '@components/CRM/Orders/OrderCard/OrderCard';

export interface TicketContainerProps {
  className?: string;
  chains: ChainType[];
  extraInfo?: ExtraInfoType;
  isCreateChain: boolean;
  isFetchChainsLoading: boolean;
  isOrdersLoading: boolean;
  isCreateTicketLoading?: boolean;
  isTicketReservationLoading: boolean;
  options: OptionsType & TourFormOptionsType;
  orders: OrderType[];
  selectedChainId: string | null;
  ticket: TicketType;
  tourForm: TourFormType | null;

  onFieldChange: TicketProps['onFieldChange'];
  onClickChain: (id: string) => void;
  onClickOrderId: IOrderCard['onClickOrderId'];
  onCreateChain: () => void;
  onDetachOrder: IOrderCard['onAttachTicket'];
  onGoToLeadMailer: () => void;
  onOrderSearch: TicketProps['onOrderSearch'];
  onReserveTicket: TicketProps['onReserveTicket'];
  onTicketClose: TicketProps['onClose'];
  onTicketCreate: TicketProps['onCreate'];
}

class TicketContainer extends React.PureComponent<TicketContainerProps> {
  static defaultProps = {
    className: '',
    onFieldChange: Function.prototype,
    onDetachOrder: Function.prototype,
    isOrdersLoading: false,
    onTicketClose: Function.prototype,
    onTicketCreate: Function.prototype,
    onClickChain: Function.prototype,
    onCreateChain: Function.prototype,
    onOrderSearch: Function.prototype,
    onClickOrderId: Function.prototype,
    onGoToLeadMailer: Function.prototype,
    options: {
      reasons: [],
      countries: [],
      closeReasons: [],
      departures: [],
      destinations: [],
      irrelevanceReasons: [],
      mailGroups: null,
      templates: [],
      managers: null,
      hotelMeals: [],
      partners: [],
      segments: [],
      sources: [],
      tags: [],
      orders: [],
      suggestedOrders: [],
      types: null,
      labels: [],
    },
    orders: null,
    ticket: null,
    selectedChainId: null,
    chains: null,
    isCreateChain: false,
    tourForm: null,
  };

  constructor(props) {
    super(props);

    this.handleTicketCreate = this.handleTicketCreate.bind(this);
  }

  handleTicketCreate(props) {
    const { orders, onTicketCreate } = this.props;
    const orderIds = (orders || []).map(order => order.id);

    onTicketCreate({ ...props, orderIds });
  }

  render() {
    const {
      onDetachOrder,
      className,
      onTicketClose,
      onFieldChange,
      onClickChain,
      onCreateChain,
      onOrderSearch,
      options,
      orders: ownOrders,
      ticket,
      isOrdersLoading,
      selectedChainId,
      isFetchChainsLoading,
      chains,
      isCreateChain,
      onClickOrderId,
      onGoToLeadMailer,
      onReserveTicket,
      isCreateTicketLoading,
      isTicketReservationLoading,
      extraInfo,
      tourForm,
    } = this.props;

    const type = get(ticket, 'type');

    const {
      reasons,
      countries,
      closeReasons,
      planReasons,
      departures,
      destinations,
      hotelMeals,
      irrelevanceReasons,
      sources,
      partners,
      segments,
      mailGroups,
      managers,
      tags,
      templates,
      types,
      orders,
      menuSources,
      suggestedOrders,
      labels,
    } = options;

    return (
      <StyledTicketContainer className={className}>
        <Ticket
          ticket={ticket}
          options={{
            reasons,
            closeReasons,
            mailGroups,
            managers,
            tags,
            templates,
            types,
            orders,
            menuSources,
            planReasons,
            suggestedOrders,
            labels,
          }}
          onCreate={this.handleTicketCreate}
          onClose={onTicketClose}
          onFieldChange={onFieldChange}
          onOrderSearch={onOrderSearch}
          onReserveTicket={onReserveTicket}
          isCreateTicketLoading={isCreateTicketLoading}
          isTicketReservationLoading={isTicketReservationLoading}
          extraInfo={extraInfo}
        />
        {type === TYPES.TOUR_SELECTION && (
          <>
            <StyledSplitter />
            <TourSelectionForm
              {...(tourForm || {})}
              options={{
                countries,
                departures,
                destinations,
                hotelMeals,
                irrelevanceReasons,
                sources,
                partners,
                segments,
              }}
              onGoToLeadMailer={onGoToLeadMailer}
            />
          </>
        )}
        {isOrdersLoading ? (
          <StyledListSkeleton />
        ) : (
          !isEmpty(ownOrders) && (
            <>
              <StyledSplitter />
              <StyledSubTitle>
                {size(ownOrders) > 1 ? 'Тикет по заказам' : 'Тикет по заказу'}
              </StyledSubTitle>
              {ownOrders.map(order => (
                <OrderCard
                  onClickOrderId={onClickOrderId}
                  key={order.id}
                  detachable
                  activeTicket={ticket}
                  ticketable={false}
                  onAttachTicket={onDetachOrder}
                  {...order}
                />
              ))}
            </>
          )
        )}
        {chains && get(ticket, 'id') && (
          <>
            <StyledSplitter />
            <StyledConversationList
              onClickCard={onClickChain}
              onCreateConversation={onCreateChain}
              selectedId={selectedChainId}
              isFetchChainsLoading={isFetchChainsLoading}
              conversations={chains}
              isCreate={isCreateChain}
              total={get(chains, 'length') || 0}
            />
          </>
        )}
      </StyledTicketContainer>
    );
  }
}

const StyledTicketContainer = styled.div`
  display: block;
`;

const StyledConversationList = styled(ConversationList2)`
  width: 100%;
`;

const StyledSubTitle = styled.span`
  color: #919499;
  display: block;
  font-size: 12px;
  margin-bottom: 7px;
`;

const StyledListSkeleton = styled(ClientOrderListSkeleton)`
  padding-top: 10px;
`;

const StyledSplitter = styled.hr`
  background: #d9d9d9;
  border: none;
  height: 1px;
  margin: 15px 0;
`;

export default TicketContainer;
